import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import Modal from '../Modal';
import filter from '../../filter';
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { FIRMS_LIST } from '../../consts/queries';
import { formatPhoneNumber } from '../../utils';

const inputValue = (v) => (v != null && typeof v !== 'object' ? v : '');

const debounceMs = 300;
const matchLimit = 8;

export function firmAddress(firm) {
    const cityState = [firm.CITY, [firm.STATE, firm.ZIP].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [firm.ADDRESS, cityState].filter(Boolean).join(', ');
}

/**
 * What the popup's fields take from a Google place: the street from the
 * number and route, the city from whichever locality level Google filled,
 * the state's two letters and the ZIP. Missing parts come back empty: a pick
 * replaces all four fields, so nothing from an earlier pick lingers.
 *
 * The street is the first line of the suggestion as it was shown, which
 * Google writes into the box on selection. The place's own details spell
 * streets differently from the list ("Blvd" where the list said
 * "Boulevard"), so the shown text wins and the components are the fallback.
 *
 * A whole road picked without a number often has no city in its details
 * even though the list said "Dew Drop Road, Bastrop, LA, USA". The shown
 * text's own city and state stand in for the missing ones.
 */
export function fieldsFromPlace(place, shownText = '') {
    const components = (place && place.address_components) || [];
    const pick = (type, key = 'long_name') => {
        const found = components.find((c) => Array.isArray(c.types) && c.types.includes(type));
        return found ? found[key] || '' : '';
    };
    const shown = String(shownText || '').split(',').map((part) => part.trim());
    const streetLine = shown[0];
    // "street, city, state, USA" — anything shorter is not a full place.
    const shownCity = shown.length >= 4 ? shown[shown.length - 3] : '';
    const shownState = shown.length >= 4 ? (shown[shown.length - 2].match(/^[A-Z]{2}\b/) || [''])[0] : '';

    return {
        ADDRESS: streetLine || [pick('street_number'), pick('route')].filter(Boolean).join(' ') || (place && place.name) || '',
        CITY: pick('locality') || pick('sublocality_level_1') || pick('postal_town') || pick('administrative_area_level_3') || shownCity,
        STATE: pick('administrative_area_level_1', 'short_name') || shownState,
        ZIP: pick('postal_code'),
    };
}

/**
 * The place's details sometimes carry no postal code even though Google's
 * geocoder knows it for the very same place. This asks the geocoder for the
 * picked place and answers with whichever of City, State and ZIP it holds.
 */
function fieldsFromGeocoder(placeId) {
    return new Promise((resolve) => {
        const maps = window.google && window.google.maps;
        if (!placeId || !maps || !maps.Geocoder) {
            resolve({});
            return;
        }

        new maps.Geocoder().geocode({ placeId }, (results, status) => {
            const first = status === 'OK' && Array.isArray(results) && results[0];
            resolve(first ? fieldsFromPlace(first) : {});
        });
    });
}

/**
 * Google's address suggestions on the Address box — the widget the parcel
 * map's search uses. Picking one fills Address, City, State and ZIP. The Maps
 * script comes from the page shell; without it the box is a plain input.
 */
function useAddressSuggestions(inputRef, onFill) {
    const fillRef = useRef(onFill);
    fillRef.current = onFill;
    const pickId = useRef(0);

    useEffect(() => {
        const node = inputRef.current;
        const places = window.google && window.google.maps && window.google.maps.places;
        if (!node || !places) return undefined;

        const autocomplete = new places.Autocomplete(node, {
            fields: ['address_components', 'name', 'place_id'],
            componentRestrictions: { country: 'us' },
            types: ['address'],
        });
        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const fields = fieldsFromPlace(place, node.value);
            const pick = ++pickId.current;
            fillRef.current(fields);

            const missing = ['CITY', 'STATE', 'ZIP'].filter((key) => !fields[key]);
            if (!missing.length) return;

            fieldsFromGeocoder(place && place.place_id).then((found) => {
                // A later pick has replaced everything; its answer is the one that counts.
                if (pick !== pickId.current) return;
                const fill = {};
                missing.forEach((key) => {
                    if (found[key]) fill[key] = found[key];
                });
                if (Object.keys(fill).length) fillRef.current(fill);
            });
        });

        return () => {
            if (listener && listener.remove) listener.remove();
        };
    }, [inputRef]);
}

/**
 * Firms already on file that match what is typed in the Firm box, by name or
 * by street — the same one-box search the investor picker uses.
 */
function useSimilarFirms(term, enabled) {
    const [matches, setMatches] = useState([]);
    const requestId = useRef(0);

    useEffect(() => {
        const typed = (term || '').trim();

        if (!enabled || typed.length < 2) {
            setMatches([]);
            return undefined;
        }

        const id = ++requestId.current;
        const timer = setTimeout(() => {
            const options = filter({ SEARCH: typed }, 'firms');
            options.param.limit = matchLimit;
            options.param.offset = 0;

            executeBackendQuery({ queryName: FIRMS_LIST, options })
                .then((res) => {
                    if (id !== requestId.current) return;
                    setMatches(res && res.success && Array.isArray(res.data) ? res.data.filter((f) => f && f.NAME) : []);
                })
                .catch(() => {
                    if (id === requestId.current) setMatches([]);
                });
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [term, enabled]);

    return matches;
}

/**
 * Looks like every other select in the app, but the options are the firms
 * already on file that match the typed text, and every one of them is
 * disabled: they are there to be seen, not picked. The typed text itself is
 * the new firm's name.
 */
function FirmNameSelect({ value, onChange, matches }) {
    return (
        <div className="dropdown-container FirmList_modal_firm">
            <Autocomplete
                className="white"
                id="firm-modal-name"
                freeSolo
                fullWidth
                disablePortal
                disableClearable
                clearOnBlur={false}
                forcePopupIcon={false}
                options={matches}
                filterOptions={(options) => options}
                getOptionDisabled={() => true}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.NAME || '')}
                renderOption={(option) => (
                    <div className="FirmList_modal_firm_option">
                        <div className="FirmList_modal_firm_option_text">
                            <span>{option.NAME}</span>
                            {firmAddress(option) ? (
                                <span className="FirmList_modal_firm_option_address">{firmAddress(option)}</span>
                            ) : (
                                <span className="FirmList_modal_firm_option_address FirmList_modal_firm_option_address--none">
                                    No address provided for this firm
                                </span>
                            )}
                        </div>
                        {/* The option itself takes no clicks; this copies the exact
                            spelling so another location is filed under the same name.
                            aria-disabled is inherited from the option, so the button
                            says otherwise for itself. */}
                        <button
                            type="button"
                            className="FirmList_modal_firm_option_use"
                            aria-disabled="false"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(option.NAME);
                            }}
                        >
                            Use this name
                        </button>
                    </div>
                )}
                value={null}
                inputValue={inputValue(value)}
                onInputChange={(e, text, reason) => {
                    if (reason === 'reset') return;
                    onChange(text || '');
                }}
                onChange={() => {}}
                noOptionsText=""
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Firm"
                        required
                        autoFocus
                        variant="outlined"
                        placeholder="Firm name or address"
                        inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    />
                )}
            />
        </div>
    );
}

function DuplicateConfirm({ message, onBack, onAddAnyway, saveError, saveBusy }) {
    return (
        <React.Fragment>
            <h2 className="FirmList_modal_title">Firm already exists</h2>
            <div className="FirmList_modal_confirm">
                <p>{message || 'This firm is already in iCLIMB.'}</p>
                <p>Do you want to add this one anyway?</p>
                {saveError && (
                    <div className="Alert Alert--error" style={{ marginBottom: 12 }}>
                        {saveError}
                    </div>
                )}
                <div className="FirmList_modal_form_footer">
                    <button type="button" className="button-new negative" onClick={onBack}>
                        Back
                    </button>
                    <button type="button" className="button-new positive" onClick={onAddAnyway} disabled={saveBusy}>
                        {saveBusy ? 'Saving…' : 'Add Anyway'}
                    </button>
                </div>
            </div>
        </React.Fragment>
    );
}

function FirmForm({ form, onFormChange, onSubmit, onClose, saveError, saveBusy, matches }) {
    const addressRef = useRef(null);

    useAddressSuggestions(addressRef, (fields) => {
        Object.keys(fields).forEach((key) => onFormChange(key, fields[key]));
    });

    return (
        <React.Fragment>
            <h2 className="FirmList_modal_title">Add Firm</h2>
            <form onSubmit={onSubmit} className="FirmList_modal_form" autoComplete="off">
                <div className="FirmList_modal_form_fields">
                    <FirmNameSelect value={form.NAME} onChange={(v) => onFormChange('NAME', v)} matches={matches} />
                    <div>
                        <label htmlFor="firm-modal-address">Address</label>
                        <input
                            id="firm-modal-address"
                            ref={addressRef}
                            type="text"
                            value={inputValue(form.ADDRESS)}
                            onChange={(e) => onFormChange('ADDRESS', e.target.value)}
                            // Enter picks the highlighted suggestion; it must not submit the form.
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') e.preventDefault();
                            }}
                            placeholder="Start typing an address"
                        />
                    </div>
                    <div className="FirmList_modal_form_row">
                        <div>
                            <label htmlFor="firm-modal-city">City</label>
                            <input
                                id="firm-modal-city"
                                type="text"
                                value={inputValue(form.CITY)}
                                onChange={(e) => onFormChange('CITY', e.target.value)}
                                placeholder="City"
                            />
                        </div>
                        <div className="FirmList_modal_form_row_state">
                            <label htmlFor="firm-modal-state">State</label>
                            <input
                                id="firm-modal-state"
                                type="text"
                                maxLength={2}
                                value={inputValue(form.STATE)}
                                onChange={(e) => onFormChange('STATE', e.target.value.toUpperCase())}
                                placeholder="TX"
                            />
                        </div>
                        <div className="FirmList_modal_form_row_zip">
                            <label htmlFor="firm-modal-zip">Zip</label>
                            <input
                                id="firm-modal-zip"
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={inputValue(form.ZIP)}
                                onChange={(e) => onFormChange('ZIP', e.target.value)}
                                placeholder="75201"
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="firm-modal-phone">Phone</label>
                        <input
                            id="firm-modal-phone"
                            type="tel"
                            value={inputValue(form.PHONE)}
                            onChange={(e) => onFormChange('PHONE', formatPhoneNumber(e.target.value))}
                            placeholder="(555) 555-5555"
                        />
                    </div>
                    <div>
                        <label htmlFor="firm-modal-website">Website</label>
                        <input
                            id="firm-modal-website"
                            type="text"
                            value={inputValue(form.WEBSITE)}
                            onChange={(e) => onFormChange('WEBSITE', e.target.value)}
                            placeholder="www.example.com"
                        />
                    </div>
                </div>
                {saveError && (
                    <div className="Alert Alert--error" style={{ marginBottom: 12 }}>
                        {saveError}
                    </div>
                )}
                <div className="FirmList_modal_form_footer">
                    <button type="button" className="button-new negative" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="button-new positive" disabled={saveBusy}>
                        {saveBusy ? 'Saving…' : 'Add Firm'}
                    </button>
                </div>
            </form>
        </React.Fragment>
    );
}

/**
 * The "+" popup on the Firms list: one form with every field, a select-style
 * Firm box whose greyed-out options are the firms already on file that match
 * the typed name or address, an Address box with Google's suggestions that
 * fill City, State and ZIP, and a yes/no prompt when the server finds the
 * firm already on file at that address on save. The same firm at a new
 * address is another location and saves without a prompt.
 */
export default function FirmModal({
    show,
    onClose,
    step,
    form,
    onFormChange,
    onSubmit,
    saveError,
    saveBusy,
    duplicateMessage,
    onBackToForm,
    onAddAnyway,
}) {
    const matches = useSimilarFirms(form.NAME, show && step === 'form');

    return (
        <Modal show={show} onClose={onClose} className="FirmList_modal">
            <div className="modal-content">
                {step === 'confirm' ? (
                    <DuplicateConfirm
                        message={duplicateMessage}
                        onBack={onBackToForm}
                        onAddAnyway={onAddAnyway}
                        saveError={saveError}
                        saveBusy={saveBusy}
                    />
                ) : (
                    <FirmForm
                        form={form}
                        onFormChange={onFormChange}
                        onSubmit={onSubmit}
                        onClose={onClose}
                        saveError={saveError}
                        saveBusy={saveBusy}
                        matches={matches}
                    />
                )}
            </div>
        </Modal>
    );
}
